import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Input,
  Select,
  Switch,
  Tooltip,
  Modal,
  Spin,
  Upload,
  message as antdMessage
} from 'antd';
import {
  PlusOutlined,
  SendOutlined,
  SettingOutlined,
  DeleteOutlined,
  EditOutlined,
  PaperClipOutlined,
  PictureOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import { aiAPI, userAPI } from '../services/api';
import config from '../config/config';
import { isSuperAdmin as isSuperAdminUser } from '../utils/auth';
import './ChatGPT.css';

const normalizeBaseURL = (baseURL) => {
  if (baseURL && baseURL.includes('https://localhost')) {
    return baseURL.replace('https://localhost', 'http://localhost');
  }
  return baseURL || 'http://localhost:3005';
};

const fetchStream = async (sessionId, payload, onDelta) => {
  const baseURL = normalizeBaseURL(config.api.baseURL);
  const token = localStorage.getItem('token');
  const response = await fetch(`${baseURL}/ai/chat/sessions/${sessionId}/stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token ? `Bearer ${token}` : ''
    },
    body: JSON.stringify(payload)
  });
  if (!response.ok || !response.body) {
    const detail = await response.text();
    throw new Error(detail || 'Stream failed');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let isDone = false;
  while (!isDone) {
    const { value, done } = await reader.read();
    isDone = done;
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const line = part.split('\n').find(l => l.startsWith('data: '));
      if (!line) continue;
      const data = line.replace('data: ', '').trim();
      if (data === '[DONE]') {
        return;
      }
      try {
        const parsed = JSON.parse(data);
        if (parsed.debug) {
          console.log('stream debug:', parsed.debug);
        } else if (parsed.content) {
          onDelta(parsed.content);
        }
      } catch {
        // ignore malformed stream chunks
      }
    }
  }
};

const parseImageMessage = (msg) => {
  if (!msg?.content) return null;
  try {
    const payload = JSON.parse(msg.content);
    if (payload?.type === 'image' && Array.isArray(payload.images)) {
      return payload;
    }
  } catch {
    // ignore invalid image payload
  }
  return null;
};

export default function ChatGPT() {
  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [models, setModels] = useState([]);
  const [plugins, setPlugins] = useState([]);
  const [selectedPlugins, setSelectedPlugins] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [input, setInput] = useState('');
  const [search, setSearch] = useState('');
  const [systemPrompt, setSystemPrompt] = useState('你是一个有用的AI助手。请优先用中文回答，必要时可附英文术语。');
  const [selectedModel, setSelectedModel] = useState('');
  const [toolsEnabled, setToolsEnabled] = useState(false);
  const [internetEnabled, setInternetEnabled] = useState(true);
  const [streaming, setStreaming] = useState(true);
  const [loading, setLoading] = useState(false);
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [renameSession, setRenameSession] = useState(null);
  const [renameLoading, setRenameLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [imageGenerating, setImageGenerating] = useState(false);
  const [pluginModalVisible, setPluginModalVisible] = useState(false);
  const [newPlugin, setNewPlugin] = useState({
    name: '',
    description: '',
    endpoint: '',
    method: 'POST',
    headers: '{}',
    schema: '{}'
  });
  const messagesEndRef = useRef(null);
  const pasteFileIndexRef = useRef(1);

  const currentSession = useMemo(
    () => sessions.find(s => s.id === selectedSessionId),
    [sessions, selectedSessionId]
  );

  const filteredSessions = useMemo(() => {
    if (!search) return sessions;
    return sessions.filter(s => s.title?.toLowerCase().includes(search.toLowerCase()));
  }, [search, sessions]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadSessions = async (keyword) => {
    const params = { q: keyword };
    if (isSuperAdminUser() && selectedUserId !== 'all') {
      params.userId = selectedUserId;
    }
    const response = await aiAPI.chat.listSessions(params);
      const list = response.data.sessions || [];
      setSessions(list);
      if (selectedSessionId && !list.find(item => item.id === selectedSessionId)) {
        setSelectedSessionId(null);
        setMessages([]);
      } else if (!selectedSessionId && list.length) {
        setSelectedSessionId(list[0].id);
      }
  };

  const loadMessages = async (sessionId) => {
    if (!sessionId) return;
    const response = await aiAPI.chat.getMessages(sessionId);
    setMessages(response.data.messages || []);
  };

  const loadModels = async () => {
    const response = await aiAPI.getModels();
    const list = response.data.models || [];
    const filtered = list.filter(item => {
      const id = String(item.id || item.name || item || '').toLowerCase();
      if (!id) return false;
      const blocked = ['tts', 'audio', 'dall-e', 'whisper', 'embedding', 'moderation'];
      if (blocked.some(tag => id.includes(tag))) return false;
      return id.includes('gpt') || id.includes('o1') || id.includes('o3');
    });
    setModels(filtered);
    if (!selectedModel && filtered.length) {
      const ids = filtered.map(item => (item.id || item.name || item)).filter(Boolean);
      const preferred = ids.find(id => id.includes('gpt-4o-mini'))
        || ids.find(id => id.includes('gpt-4o'))
        || ids[0];
      setSelectedModel(preferred);
    }
  };

  const loadPlugins = async () => {
    const response = await aiAPI.chat.listPlugins();
    setPlugins(response.data.plugins || []);
  };

  const loadUsers = async () => {
    if (!isSuperAdminUser()) return;
    const response = await userAPI.getUsers();
    setUsers(response.data || []);
  };

  useEffect(() => {
    loadSessions();
    loadModels();
    loadPlugins();
    loadUsers();
  }, []);

  useEffect(() => {
    if (selectedSessionId) {
      loadMessages(selectedSessionId);
      const session = sessions.find(s => s.id === selectedSessionId);
      if (session?.systemPrompt) setSystemPrompt(session.systemPrompt);
      if (session?.model) setSelectedModel(session.model);
    }
  }, [selectedSessionId]);

  useEffect(() => {
    if (isSuperAdminUser()) {
      loadSessions(search);
    }
  }, [selectedUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleNewChat = async () => {
    const response = await aiAPI.chat.createSession({
      title: 'New Chat',
      model: selectedModel || undefined,
      systemPrompt
    });
    const session = response.data.session;
    setSessions(prev => [session, ...prev]);
    setSelectedSessionId(session.id);
    setMessages([]);
  };

  const handleRename = async () => {
    if (!renameSession) return;
    const newTitle = (renameSession.title || '').trim();
    if (!newTitle) {
      antdMessage.warning('请输入对话标题');
      return;
    }
    setRenameLoading(true);
    try {
      const response = await aiAPI.chat.updateSession(renameSession.id, { title: newTitle });
      setSessions(prev => prev.map(s => (s.id === renameSession.id ? response.data.session : s)));
      setRenameSession(null);
    } catch {
      antdMessage.error('重命名失败，请检查后端连接');
    } finally {
      setRenameLoading(false);
    }
  };

  const handleArchiveSession = async (id) => {
    await aiAPI.chat.archiveSession(id);
    setSessions(prev => prev.filter(s => s.id !== id));
    if (selectedSessionId === id) {
      setSelectedSessionId(null);
      setMessages([]);
    }
  };

  const fixFileMime = (file) => {
    if (!file) return file;
    const type = file.type || '';
    if (type.startsWith('image/')) return file;
    const name = (file.name || '').toLowerCase();
    const ext = name.split('.').pop();
    const map = {
      png: 'image/png',
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      webp: 'image/webp',
      gif: 'image/gif',
      bmp: 'image/bmp'
    };
    const mime = map[ext];
    if (!mime) return file;
    return new File([file], file.name, {
      type: mime,
      lastModified: file.lastModified || Date.now()
    });
  };

  const handleUploadFiles = async (sessionId) => {
    if (!fileList.length) return [];
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('sessionId', sessionId);
      fileList.forEach(file => {
        const fixed = fixFileMime(file.originFileObj);
        formData.append('files', fixed);
      });
      const response = await aiAPI.chat.uploadFiles(formData);
      setFileList([]);
      return response.data.files || [];
    } finally {
      setUploading(false);
    }
  };

  const handlePaste = (event) => {
    const items = event.clipboardData?.items || [];
    const pastedFiles = [];

    for (const item of items) {
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const blob = item.getAsFile();
        if (!blob) continue;
        const fileName = `pasted-image-${Date.now()}-${pasteFileIndexRef.current++}.png`;
        const file = new File([blob], fileName, { type: blob.type || 'image/png' });
        pastedFiles.push(file);
      }
    }

    if (pastedFiles.length) {
      event.preventDefault();
      setFileList(prev => [
        ...prev,
        ...pastedFiles.map(file => ({
          uid: `${file.name}-${file.lastModified}`,
          name: file.name,
          status: 'done',
          originFileObj: file
        }))
      ]);
      antdMessage.success(`已添加 ${pastedFiles.length} 张图片`);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    setLoading(true);
    try {
      let sessionId = selectedSessionId;
      if (!sessionId) {
        const response = await aiAPI.chat.createSession({
          title: 'New Chat',
          model: selectedModel || undefined,
          systemPrompt
        });
        sessionId = response.data.session.id;
        setSessions(prev => [response.data.session, ...prev]);
        setSelectedSessionId(sessionId);
      }

      const uploadedFiles = await handleUploadFiles(sessionId);
      const fileIds = uploadedFiles.map(f => f.id);

      const userMessage = {
        id: `temp-user-${Date.now()}`,
        role: 'user',
        content: input
      };
      const assistantMessageId = `temp-assistant-${Date.now()}`;
      setMessages(prev => [...prev, userMessage, { id: assistantMessageId, role: 'assistant', content: '' }]);

      const payload = {
        content: input,
        model: selectedModel || undefined,
        systemPrompt,
        fileIds,
        toolsEnabled,
        internetEnabled,
        pluginIds: selectedPlugins,
        toolChoice: toolsEnabled ? 'auto' : undefined
      };

      setInput('');

      if (streaming) {
        await fetchStream(sessionId, payload, (delta) => {
          setMessages(prev =>
            prev.map(msg => (msg.id === assistantMessageId ? { ...msg, content: msg.content + delta } : msg))
          );
        });
        await loadMessages(sessionId);
        await loadSessions(search);
      } else {
        await aiAPI.chat.sendMessage(sessionId, payload);
        await loadMessages(sessionId);
        await loadSessions(search);
      }
    } catch {
      antdMessage.error('发送失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!input.trim()) return;
    setImageGenerating(true);
    try {
      let sessionId = selectedSessionId;
      if (!sessionId) {
        const response = await aiAPI.chat.createSession({
          title: 'New Chat',
          model: selectedModel || undefined,
          systemPrompt
        });
        sessionId = response.data.session.id;
        setSessions(prev => [response.data.session, ...prev]);
        setSelectedSessionId(sessionId);
      }
      await aiAPI.chat.generateImage(sessionId, {
        prompt: input.trim(),
        size: '1024x1024',
        quality: 'standard'
      });
      setInput('');
      await loadMessages(sessionId);
      await loadSessions(search);
    } catch {
      antdMessage.error('图片生成失败');
    } finally {
      setImageGenerating(false);
    }
  };

  const handleCreatePlugin = async () => {
    try {
      const headersText = newPlugin.headers?.trim();
      const schemaText = newPlugin.schema?.trim();
      const headers = headersText ? JSON.parse(headersText) : undefined;
      const schema = schemaText ? JSON.parse(schemaText) : undefined;
      await aiAPI.chat.createPlugin({
        name: newPlugin.name,
        description: newPlugin.description,
        endpoint: newPlugin.endpoint,
        method: newPlugin.method,
        headers,
        schema,
        enabled: true
      });
      setPluginModalVisible(false);
      setNewPlugin({
        name: '',
        description: '',
        endpoint: '',
        method: 'POST',
        headers: '{}',
        schema: '{}'
      });
      await loadPlugins();
      antdMessage.success('插件已添加');
    } catch {
      antdMessage.error('插件创建失败，请检查 JSON 配置');
    }
  };

  return (
    <div className="chatgpt-container">
      <aside className="chatgpt-sidebar">
        <div className="chatgpt-sidebar-header">
          <Button type="primary" icon={<PlusOutlined />} onClick={handleNewChat} block>
          </Button>
          <Input
            placeholder="搜索对话"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onPressEnter={() => loadSessions(search)}
          />
          {isSuperAdminUser() && (
            <Select
              value={selectedUserId}
              onChange={(value) => setSelectedUserId(value)}
              options={[
                { value: 'all', label: '全部用户' },
                ...users.map(user => ({
                  value: String(user.id),
                  label: `${user.username} (ID:${user.id})`
                }))
              ]}
            />
          )}
        </div>
        <div className="chatgpt-session-list">
          {filteredSessions.map(session => (
            <div
              key={session.id}
              className={`chatgpt-session-item ${session.id === selectedSessionId ? 'active' : ''}`}
              onClick={() => setSelectedSessionId(session.id)}
            >
              <div className="chatgpt-session-title">{session.title || 'New Chat'}</div>
              <div className="chatgpt-session-actions">
                <Tooltip title="重命名">
                  <Button
                    size="small"
                    type="text"
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameSession({ id: session.id, title: session.title || '' });
                    }}
                  />
                </Tooltip>
                <Tooltip title="归档">
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleArchiveSession(session.id);
                    }}
                  />
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      </aside>

      <main className="chatgpt-main">
        <header className="chatgpt-main-header">
          <div>
            <div className="chatgpt-title">{currentSession?.title || ''}</div>
          </div>
          <div className="chatgpt-header-actions">
            <Select
              className="chatgpt-model-select"
              value={selectedModel}
              onChange={setSelectedModel}
              placeholder="选择模型"
              options={models.map(m => ({
                value: m.id || m.name || m,
                label: m.id || m.name || m
              }))}
              showSearch
            />
            <Button icon={<SettingOutlined />} onClick={() => setSettingsVisible(true)} />
          </div>
        </header>

        <section className="chatgpt-messages">
          {messages.map(msg => {
            const imagePayload = parseImageMessage(msg);
            return (
              <div key={msg.id} className={`chatgpt-message ${msg.role}`}>
                <div className="chatgpt-message-bubble">
                  {imagePayload ? (
                    <div className="chatgpt-image-grid">
                      {imagePayload.images.map((img, index) => (
                        <img key={index} src={img.url} alt={imagePayload.prompt || 'generated'} />
                      ))}
                    </div>
                  ) : (
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {msg.content || ''}
                    </ReactMarkdown>
                  )}
                </div>
              </div>
            );
          })}
          {(loading || uploading) && (
            <div className="chatgpt-message assistant">
              <div className="chatgpt-message-bubble">
                <Spin size="small" /> 正在思考...
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </section>

        <footer className="chatgpt-composer">
          <div className="chatgpt-composer-actions">
            <Upload
              multiple
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList: newList }) => setFileList(newList)}
            >
              <Button icon={<PaperClipOutlined />} />
            </Upload>
            <Button icon={<PictureOutlined />} onClick={handleGenerateImage} loading={imageGenerating} />
            <div className="chatgpt-toggle">
              <Switch checked={toolsEnabled} onChange={setToolsEnabled} />
            </div>
            <div className="chatgpt-toggle">
              <Switch checked={internetEnabled} onChange={setInternetEnabled} />
            </div>
            <div className="chatgpt-toggle">
              <Switch checked={streaming} onChange={setStreaming} />
            </div>
          </div>
          <div className="chatgpt-input-row">
            <Input.TextArea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder=""
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
            />
            <Button type="primary" icon={<SendOutlined />} onClick={handleSend} loading={loading} />
          </div>
        </footer>
      </main>

      <Modal
        title="对话设置"
        open={settingsVisible}
        onCancel={() => setSettingsVisible(false)}
        onOk={async () => {
          if (currentSession?.id) {
            await aiAPI.chat.updateSession(currentSession.id, {
              systemPrompt,
              model: selectedModel
            });
            await loadSessions(search);
          }
          setSettingsVisible(false);
        }}
      >
        <div className="chatgpt-settings">
          <div>
            <div className="chatgpt-settings-label">系统提示词</div>
            <Input.TextArea rows={5} value={systemPrompt} onChange={(e) => setSystemPrompt(e.target.value)} />
          </div>
          <div>
            <div className="chatgpt-settings-label">已启用插件</div>
            <Select
              mode="multiple"
              value={selectedPlugins}
              onChange={setSelectedPlugins}
              style={{ width: '100%' }}
              placeholder="选择插件"
              options={plugins.map(plugin => ({
                value: plugin.id,
                label: plugin.name
              }))}
            />
            <div style={{ marginTop: 8 }}>
              <Button type="dashed" onClick={() => setPluginModalVisible(true)}>
                新增插件
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title="重命名对话"
        open={!!renameSession}
        onCancel={() => setRenameSession(null)}
        onOk={handleRename}
        confirmLoading={renameLoading}
      >
        <Input
          value={renameSession?.title || ''}
          onChange={(e) => setRenameSession(prev => ({ ...prev, title: e.target.value }))}
          placeholder="输入对话标题"
        />
      </Modal>

      <Modal
        title="新增插件"
        open={pluginModalVisible}
        onCancel={() => setPluginModalVisible(false)}
        onOk={handleCreatePlugin}
      >
        <div className="chatgpt-settings">
          <Input
            placeholder="插件名称"
            value={newPlugin.name}
            onChange={(e) => setNewPlugin(prev => ({ ...prev, name: e.target.value }))}
          />
          <Input
            placeholder="插件描述"
            value={newPlugin.description}
            onChange={(e) => setNewPlugin(prev => ({ ...prev, description: e.target.value }))}
          />
          <Input
            placeholder="请求地址 (https://...)"
            value={newPlugin.endpoint}
            onChange={(e) => setNewPlugin(prev => ({ ...prev, endpoint: e.target.value }))}
          />
          <Select
            value={newPlugin.method}
            onChange={(value) => setNewPlugin(prev => ({ ...prev, method: value }))}
            options={[
              { value: 'POST', label: 'POST' },
              { value: 'GET', label: 'GET' }
            ]}
          />
          <Input.TextArea
            rows={3}
            placeholder="Headers JSON"
            value={newPlugin.headers}
            onChange={(e) => setNewPlugin(prev => ({ ...prev, headers: e.target.value }))}
          />
          <Input.TextArea
            rows={4}
            placeholder="Schema JSON (OpenAI tool schema)"
            value={newPlugin.schema}
            onChange={(e) => setNewPlugin(prev => ({ ...prev, schema: e.target.value }))}
          />
        </div>
      </Modal>
    </div>
  );
}
